import { DsIcon } from '@descomplica/design-system-react';
import { SuggestionSelect } from '@descomplica/foundation-ui-components';
import { useViewModel } from '@descomplica/microfrontend-react';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { OpenLinkIcon } from '@/modules/recognition/admin/presentation/assets/Icons';
import { ApplicationStatus } from '@/shared/application/interfaces/pegasus-io.gateway';
import { Button } from '@/shared/presentation/components/ui/Button';
import { DataTable } from '@/shared/presentation/components/ui/DataTable';
import { FormModal } from '@/shared/presentation/components/ui/FormModal';
import { Input } from '@/shared/presentation/components/ui/Input';
import { LoadingModal } from '@/shared/presentation/components/ui/LoadingDialog';
import { Page } from '@/shared/presentation/components/ui/Page';
import { Textarea } from '@/shared/presentation/components/ui/Textarea';

import { ApplicationsViewModel } from './Applications.vm';

type Props = {
  status: ApplicationStatus;
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`;

const AppStatusWrapper = styled.div<Props>`
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const AppStatusContentWrapper = styled.div<Props>`
  display: flex;
  justify-content: center;
  align-items: center;
  color: #ff7572;

  .ds-icon {
    background-color: #ff7572;
    background-color: rgb(0, 248, 143);
    margin-top: -1px;
    margin-right: 4px;
  }
`;

const AppLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AppStatus: React.FC<Props> = ({ status }) => {
  return (
    <AppStatusWrapper status={status}>
      <AppStatusContentWrapper status={status}>
        {status === 'AVAILABLE' && (
          <span title="Disponível">
            <DsIcon image="circle-check" />
          </span>
        )}
        {status === 'UNAVAILABLE' && (
          <span title="Indisponível">
            <DsIcon image="circle-uncheck" />
          </span>
        )}
      </AppStatusContentWrapper>
    </AppStatusWrapper>
  );
};

export default AppStatus;

const ApplicationForm = observer(({ isEditing }: { isEditing?: boolean }) => {
  const vm = useViewModel(ApplicationsViewModel);

  return (
    <Form>
      <Input
        label="Nome"
        name="name"
        errorMessage={vm.form.errors.name && vm.form.errors.name[0]}
        onChange={(value) => vm.form.setValue('name', value)}
        onBlur={() => vm.form.validateField('name')}
        value={vm.form.values.name}
        disabled={vm.isUpdating}
      />
      <SuggestionSelect
        label="Organização"
        placeholder="Definir organização"
        onChange={(value) => {
          vm.form.setValueAndValidateValue('organization', value);
        }}
        value={vm.form.values.organization}
        availableOptions={vm.organizations.map((organization) => ({
          guid: organization.guid,
          name: organization.name,
        }))}
        defaultValue={vm.form.values.organization}
      />
      <Input
        label="Subdomínio"
        name="subdomain"
        errorMessage={vm.form.errors.subdomain && vm.form.errors.subdomain[0]}
        onChange={(value) => vm.form.setValue('subdomain', value)}
        onBlur={() => vm.form.validateField('subdomain')}
        value={vm.form.values.subdomain}
        disabled={vm.isUpdating}
      />
      <span>
        A URL de acesso será: https://{vm.form.values.subdomain || '[subdomínio]'}
        {vm.baseDomain}
      </span>
      <Textarea
        label="Descrição"
        name="description"
        errorMessage={vm.form.errors.description && vm.form.errors.description[0]}
        onChange={(value) => vm.form.setValue('description', value)}
        onBlur={() => vm.form.validateField('description')}
        value={vm.form.values.description}
      />
    </Form>
  );
});

const CreateApplication = observer(() => {
  const vm = useViewModel(ApplicationsViewModel);

  return (
    <FormModal
      title="Adicionar aplicação"
      onClose={() => vm.closeCreateModal()}
      onSubmit={() => vm.create()}
      isSubmitting={vm.isSubmitting}
      isValid={vm.form.isValid}
    >
      <ApplicationForm />
    </FormModal>
  );
});

const UpdateApplication = observer(() => {
  const vm = useViewModel(ApplicationsViewModel);

  return (
    <FormModal
      title="Editar aplicação"
      onClose={() => vm.closeUpdateModal()}
      onSubmit={() => vm.update()}
      isSubmitting={vm.isUpdating}
      isValid={vm.form.isValid}
    >
      <ApplicationForm isEditing />
    </FormModal>
  );
});

const NormalSpan = styled.span`
  white-space: normal;
`;

export const ApplicationsScreen = observer(() => {
  const vm = useViewModel(ApplicationsViewModel);

  const columns = useMemo(
    () => [
      {
        name: 'GUID',
        grow: 2,
        selector: (row) => row.guid,
        format: (row) => <NormalSpan>{row.guid}</NormalSpan>,
      },
      {
        name: 'Nome',
        selector: (row) => row.name,
        format: (row) => <Link to={`/admin/applications/${row.guid}`}>{row.name}</Link>,
        sortable: true,
      },
      {
        name: 'Descrição',
        grow: 1,
        selector: (row) => row.description || '',
        format: (row) => <span title={row.description}>{row.description}</span>,
      },
      {
        name: 'Organização',
        center: true,
        grow: 1,
        selector: (row) => row.organizationName || '',
        format: (row) => (
          <img style={{ height: '32px' }} src={row.organization.logo} alt={row.organizationName} />
        ),
      },
      {
        name: 'URL',
        grow: 4,
        selector: (row) => row.baseURL,
        format: (row) => (
          <AppLink href={`https://${row.baseURL}`} target="_blank" rel="noreferrer">
            {row.baseURL}
            <OpenLinkIcon />
          </AppLink>
        ),
      },
      {
        name: 'Status',
        selector: (row) => row.status,
        format: (row) => <AppStatus status={row.status} />,
        sortable: true,
        button: true,
      },
    ],
    [],
  );

  const actions = useMemo(
    () => [
      {
        name: 'Editar',
        onClick: (row) => {
          vm.openUpdateModal(row);
        },
      },
      {
        name: 'Excluir',
        onClick: (row) => {
          const confirm = window.confirm(
            'Tem certeza que deseja excluir? Os dados do LMS serão removidos para sempre',
          );
          if (confirm) {
            vm.remove(row.guid, row.scopeGuid);
          }
        },
      },
    ],
    [],
  );

  return (
    <Page
      title="Aplicações"
      subtitle="Gerencie as aplicações"
      isLoading={vm.isLoading}
      hasError={vm.hasError}
      errorMessage={'Ocorreu um erro ao carregar as aplicações.'}
      headerActions={() => (
        <>
          <Button icon="refresh" disabled={vm.isUpdating} onClick={() => vm.refreshApplications()}>
            Atualizar
          </Button>{' '}
          <Button icon="add" disabled={vm.isLoading} onClick={() => vm.openCreateModal()}>
            Adicionar
          </Button>
        </>
      )}
    >
      <DataTable
        isLoading={vm.isLoading}
        columns={columns}
        data={vm.applications}
        actions={actions}
      />

      {vm.isCreateModalOpen && <CreateApplication />}
      {vm.isUpdateModalOpen && <UpdateApplication />}
      {vm.isRemoving && <LoadingModal text="Removendo aplicação..." />}
    </Page>
  );
});
